import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { exigeMotivo, validarEntrada } from '@/leads/valores';

/**
 * ★★ WD-c2 — OS CINCO CAMPOS DE VALOR DO FECHAMENTO, num componente SÓ.
 *
 * ★ ELE EXISTE UMA VEZ E É USADO EM DOIS MOMENTOS:
 *   1. ao **mover o lead para "Fechou"** (dentro do `DialogoEtapa`);
 *   2. ao clicar em **"Editar"** no bloco de negociação (dentro do `DialogoValores`).
 *
 * Não são duas telas parecidas: é **a mesma**. Duas cópias divergiriam no primeiro ajuste — uma ganharia uma
 * validação que a outra não tem, e o mesmo lead teria regras diferentes conforme o caminho. Há guarda de
 * fonte (`web/src/leads/wd.guardas.test.js`) que falha se alguém duplicar os campos, e a mutação que os
 * duplica (D24) morre nela.
 *
 * ★ O componente NÃO decide nada: quem decide é `@/leads/valores` — `exigeMotivo` diz quando o motivo é
 * obrigatório, `validarEntrada` valida a entrada.
 */
/**
 * ★★ WH (29/set/2026) — O TERCEIRO MOMENTO: `momento="comparecimento"`.
 *
 * "Um componente, dois momentos" virou TRÊS, e por isso ele ganhou um MODO
 * declarado em vez de uma cópia. No comparecimento não existe total nem entrada
 * (o lead não fechou nada), e existem duas caixas que só fazem sentido ali. A
 * alternativa — acrescentar "Código Paciente" e "Nome" ao `DialogoEtapa` — criaria
 * dois lugares com o MESMO campo, que é exatamente a divergência que a guarda de
 * fonte da WD-c2 existe para impedir.
 *
 *   'fechamento'     (padrão) os cinco valores + motivo — fechar e editar
 *   'comparecimento' oportunidade + código + nome + as duas caixas
 */
export default function CamposValores({
  valores, aoMudar, lead = null, clinica = null, disabled = false, autoFocus = false,
  momento = 'fechamento',
}) {
  const sistema = nomeDoSistema(clinica?.config);
  const set = (campo) => (e) => aoMudar({ ...valores, [campo]: e.target.value });
  const marcar = (campo) => (e) => aoMudar({ ...valores, [campo]: e.target.checked });
  const noComparecimento = momento === 'comparecimento';

  // ★ "fechar" não tem total anterior; "editar" tem. A regra é a mesma função nos dois casos.
  const pedeMotivo = exigeMotivo({
    valorFechadoAnterior: lead?.valorFechado,
    valorFechadoNovo: valores.valorFechado,
  });
  const erroEntrada = validarEntrada({
    valorFechado: valores.valorFechado,
    valorEntrada: valores.valorEntrada,
  });

  return (
    <div className="campos-valores">
      <div className="linha">
        <div className="campo">
          <label htmlFor="cv-orcado">Valor de oportunidade (R$)</label>
          <input id="cv-orcado" inputMode="decimal" placeholder="ex.: 12000" disabled={disabled}
            value={valores.valorOrcado} onChange={set('valorOrcado')} />
        </div>
        {/* ★ WH: total e entrada não existem no comparecimento — o lead não fechou nada. */}
        {!noComparecimento && (
          <>
            <div className="campo">
              <label htmlFor="cv-total">Total da venda (R$)</label>
              <input id="cv-total" inputMode="decimal" placeholder="18500" disabled={disabled}
                autoFocus={autoFocus} value={valores.valorFechado} onChange={set('valorFechado')} />
            </div>
            <div className="campo">
              <label htmlFor="cv-entrada">Entrada paga no ato (R$)</label>
              <input id="cv-entrada" inputMode="decimal" disabled={disabled}
                value={valores.valorEntrada} onChange={set('valorEntrada')} />
            </div>
          </>
        )}
      </div>

      {/**
        * ★★ WH — A CAIXA "COMPARECEU SEM ORÇAMENTO".
        *
        * Palavras do dono: "em casos muito raros, pode acontecer da pessoa comparecer e
        * não ser realizado o orçamento… para a SDR não ser prejudicada". Ela dispensa
        * **só o valor** — código e nome seguem obrigatórios, porque quem compareceu tem
        * ficha no sistema da clínica.
        *
        * ★ Marcar LIMPA o campo do valor: deixar um número digitado embaixo de uma caixa
        * que diz "não houve orçamento" é o tipo de contradição que alguém grava sem ver.
        */}
      {noComparecimento && (
        <label className="campo-caixa" htmlFor="cv-sem-orcamento">
          <input
            type="checkbox"
            id="cv-sem-orcamento"
            disabled={disabled}
            checked={valores.semOrcamento === true}
            onChange={(e) => aoMudar({
              ...valores,
              semOrcamento: e.target.checked,
              ...(e.target.checked ? { valorOrcado: '' } : {}),
            })}
          />
          <span>Compareceu sem orçamento</span>
        </label>
      )}

      <div className="linha">
        <div className="campo">
          <label htmlFor="cv-codigo">Código Paciente</label>
          <input id="cv-codigo" placeholder="ex.: 48210" disabled={disabled}
            value={valores.codigoCareflow} onChange={set('codigoCareflow')} />
        </div>
        <div className="campo">
          <label htmlFor="cv-nome">Nome completo no {sistema}</label>
          <input id="cv-nome" placeholder="Como está cadastrado na clínica" disabled={disabled}
            value={valores.nome} onChange={set('nome')} />
        </div>
      </div>

      {/**
        * ★★ WH — O NOME É UM GESTO, NÃO UM JULGAMENTO.
        *
        * Medido em `docs/wh-medicao.md`: **não dá** para o sistema saber se um nome está
        * atualizado — `Laura` e `Dinalea` são legítimos, e havia paciente chamado `🎼`. As
        * três saídas foram pesadas: campo vazio obrigando a redigitar 59 nomes por mês é
        * hostil; campo pré-preenchido sem mais nada é o que JÁ EXISTE e é o que falhou.
        * Ficou a CONFIRMAÇÃO: um clique, que transforma o padrão em ATO e deixa registro
        * (`nomeConfirmadoEm`).
        *
        * ★ E o limite, dito onde o código mora: uma caixa pode ser marcada sem ler. O que
        * ela compra é um ato e um registro — não uma garantia. A garantia exigiria
        * conferir contra o sistema da clínica, integração que não existe.
        */}
      {noComparecimento && (
        <label className="campo-caixa" htmlFor="cv-nome-confirmado">
          <input
            type="checkbox"
            id="cv-nome-confirmado"
            disabled={disabled}
            checked={valores.nomeConfirmado === true}
            onChange={marcar('nomeConfirmado')}
          />
          <span>Confirmo que o nome está como cadastrado no {sistema}</span>
        </label>
      )}

      {/* ★ O componente só MOSTRA o diagnóstico; quem decide se ele trava é o diálogo que hospeda.
          Ao editar (`DialogoValores`) trava; ao fechar (`DialogoEtapa`) não — ver `validarEntrada`. */}
      {erroEntrada && <p className="aviso cv-aviso-entrada">{erroEntrada}</p>}

      {/* ★ O motivo só aparece quando é exigido — e a regra de quando é `exigeMotivo`, não um `if` daqui. */}
      {pedeMotivo && (
        <div className="campo">
          <label htmlFor="cv-motivo">Motivo da correção do total</label>
          <textarea id="cv-motivo" rows={2} disabled={disabled}
            placeholder="Ex.: o valor do orçamento foi digitado no lugar do total"
            value={valores.motivo} onChange={set('motivo')} />
          <p className="campo-ajuda">
            O total da venda entra em metas, painel e placar — inclusive de meses passados. A correção fica
            registrada na timeline, com o de-para e o motivo.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * O estado inicial dos cinco campos, a partir do lead.
 *
 * ★ WD-c1, preservado — código e nome nascem PREENCHIDOS com o que o lead já tem, como VALOR editável (o
 * nome já foi `placeholder`: sugestão que sumia ao digitar e não ia para o banco). A pessoa CONFERE ou
 * COMPLETA, em vez de redigitar o que o sistema já sabe.
 */
export const valoresIniciais = (lead = null) => ({
  valorOrcado: Number(lead?.valorOrcado) > 0 ? String(lead.valorOrcado) : '',
  valorFechado: Number(lead?.valorFechado) > 0 ? String(lead.valorFechado) : '',
  valorEntrada: Number(lead?.valorEntrada) > 0 ? String(lead.valorEntrada) : '',
  codigoCareflow: lead?.codigoCareflow ?? '',
  nome: lead?.nome ?? '',
  motivo: '',
  // ★ WH: a confirmação do nome NASCE FALSA sempre — é o gesto que se pede a cada
  // entrada em "compareceu". Herdá-la do lead transformaria o ato em padrão, que é
  // exatamente o que a medição mostrou não funcionar.
  nomeConfirmado: false,
  // ★ Já a marca "sem orçamento" nasce do lead: quem já foi dispensado uma vez não
  // é perguntado de novo (e é o que faz `exigeCampoExtra` parar de abrir o diálogo).
  semOrcamento: lead?.semOrcamento === true,
});
